import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generalErrorHandle } from "../../actions";
import {
  fetchTopSearches,
  deleteTopSearch,
  addOrEditTopSearch,
} from "../../actions/topSearchActions";
import TopSearchSortable from "./TopSearchSortable";
import Swal from "sweetalert2";
import { Button, Container, Typography, Box, CircularProgress } from "@mui/material";

const TopSearch = () => {
  const dispatch = useDispatch();
  const topSearchFromStore = useSelector((state) => state.search.top_search_data);
  const [topSearchData, setTopSearchData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchTopSearches());
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: "Failed to fetch top searches. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  // Update topSearchData when store data changes
  useEffect(() => {
    if (topSearchFromStore) {
      setTopSearchData(topSearchFromStore);
    }
  }, [topSearchFromStore]);

  // Handle deleting an item
  const handleDelete = useCallback(async (e, top_search_id, index) => {
    e.preventDefault();
    try {
      if (top_search_id) {
        await dispatch(deleteTopSearch(top_search_id));
      }
      const updatedData = [...topSearchData];
      updatedData.splice(index, 1);
      setTopSearchData(updatedData);
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: "Failed to delete item. Please try again.",
      });
    }
  }, [dispatch, topSearchData]);

  // Handle input change
  const handleChange = useCallback((e, index) => {
    const value = e.target.value;
    setTopSearchData(prevData => {
      const updatedData = [...prevData];
      updatedData[index] = { ...updatedData[index], value };
      return updatedData;
    });
  }, []);

  // Add a new top search item
  const addNewTopSearch = useCallback(() => {
    const hasUnsavedItem = topSearchData.some((item) => item.top_search_id === undefined);

    if (!hasUnsavedItem) {
      setTopSearchData(prevData => [
        ...prevData,
        {
          value: "",
          sort_order: prevData.length + 1,
        }
      ]);
    } else {
      Swal.fire({
        icon: "error",
        text: "Please save the current new item before adding another."
      });
    }
  }, [topSearchData]);

  // Validate data before submitting
  const validateData = useCallback(() => {
    const invalidItem = topSearchData.find(data => !data.value);
    if (invalidItem) {
      throw new Error("Please fill all missing data for top search");
    }
  }, [topSearchData]);

  // Handle form submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      validateData();
      await dispatch(addOrEditTopSearch(JSON.stringify(topSearchData)));
      Swal.fire({
        icon: "success",
        text: "Top searches updated successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: error.message || "Failed to update top searches. Please try again.",
      });
    }
  }, [dispatch, topSearchData, validateData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      {/* Sortable Items Section */}
      <Box>
        {topSearchData.length > 0 && (
          <TopSearchSortable
            items={topSearchData}
            setItems={setTopSearchData}
            onDelete={handleDelete}
            handleChange={handleChange}
          />
        )}
      </Box>

      {/* Action Buttons Section */}
      <Box my={4} display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          color="primary"
          onClick={addNewTopSearch}
          disabled={topSearchData.some(item => item.top_search_id === undefined)}
        >
          Add New
        </Button>
      </Box>

      {/* Save Button Section */}
      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{
            width: {
              xs: '100%',  // Full width on mobile
              sm: '50%',   // Half width on tablet
              md: '35%'    // 35% width on desktop
            }
          }}
        >
          Save
        </Button>
      </Box>
    </Container>
  );
};

export default TopSearch;
